/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { Logger, UsernameUtil } from "@html-core";
import { ShareFolderBaseModel } from "../../../native-shared/base/shared-folder-model-base";
import { FileSystemService } from "../../../shared/desktop/cdr/cdr-lib/file-system-service";
import { ConnectionServerModel } from "../../../shared/common/model/connection-server-model";
import { UserInfoService } from "../../../shared/common/service/user-info.service";

export interface FolderInfo {
   path: string;
   valid: boolean;
   id: string;
   htmlEntry?: any;
}

export interface DirectoryInfo {
   path: string;
   directoryHandler: any;
}

@Injectable({
   providedIn: "root"
})
export class IwaShareFolderModel extends ShareFolderBaseModel {
   private _logger;
   private _directoryMap = new Map<string, DirectoryInfo>();
   private _entryArray = new Array<FileSystemDirectoryHandle>();
   private _FAFolder = null;
   public isContainsFA;

   constructor(
      private fileSystemService: FileSystemService,
      private connectionServerModel: ConnectionServerModel,
      private userInfoService: UserInfoService
   ) {
      super();
      this._logger = new Logger(Logger.CDR);
   }

   /*
    *---------------------------------------------------------------------------
    * getDisplayInfo --
    *
    * Get the folder info when cdr window shows, if there is no entries stored on
    * client side, get it from indexedDB, if there entries stored on client side,
    * directly use them.
    *
    * Results:
    *      An array of FolderInfo
    *---------------------------------------------------------------------------
    */
   public getDisplayInfo = (): Promise<Array<FolderInfo>> => {
      return new Promise((resolve) => {
         let result = new Array<FolderInfo>();
         if (this._directoryMap.size === 0) {
            this._getFolderInfoFromStorage()
               .then((folderArray: Array<any>) => {
                  folderArray?.forEach((folder) => {
                     this._logger.debug("Shared folder got from indexedDB: " + folder.path);
                     result.push({
                        path: folder.path,
                        valid: !!folder.entry,
                        htmlEntry: folder.entry,
                        id: folder.id
                     });
                     this._directoryMap.set(folder.id, {
                        path: folder.path,
                        directoryHandler: folder.entry
                     });
                     this._entryArray.push(folder.entry);
                  });
                  this._logger.info("Successfully get the shared folder from indexedDB.");

                  resolve(result);
               })
               .catch((e) => {
                  this._logger.info("Failed to get folder info from storage");
                  this._logger.error(e);
                  resolve([]);
               });
         } else {
            this._directoryMap.forEach((entryInfo, key) => {
               result.push({
                  path: entryInfo.path,
                  valid: !!entryInfo,
                  htmlEntry: entryInfo.directoryHandler,
                  id: key
               });
            });
            resolve(result);
         }
      });
   };

   /*
    *---------------------------------------------------------------------------
    * _getFolderInfoFromStorage --
    *
    * Get the folder entries stored in indexedDB
    *
    * Results:
    *      None
    *---------------------------------------------------------------------------
    */

   private _getFolderInfoFromStorage = () => {
      const cdrFeatureKey = this._getKeyName();
      return new Promise((resolve) => {
         try {
            this.fileSystemService
               .getDirectoryEntryFromStorage(cdrFeatureKey)
               .then((res: any) => {
                  resolve(res);
               })
               .catch((e) => {
                  this._logger.debug("getDirectoryEntryFromStorage return error with " + cdrFeatureKey);
                  this._logger.exception(e);
                  resolve([]);
               });
         } catch (e) {
            this._logger.exception(e);
            this._logger.debug("getDirectoryEntryFromStorage failed with " + cdrFeatureKey);
            resolve([]);
         }
      });
   };

   /*
    *---------------------------------------------------------------------------
    * save  --
    *
    * Save entry info of temporary variable to dataBase when clicking save button
    *
    * Results:
    *      None.
    *---------------------------------------------------------------------------
    */
   public save = (folderInfoArray) => {
      return new Promise((resolve) => {
         this._entryArray = [];
         folderInfoArray.forEach((folderInfo) => {
            this._entryArray.push(folderInfo.entry.directoryHandler);
            this._directoryMap.set(folderInfo.id, {
               path: folderInfo.path,
               directoryHandler: folderInfo.entry
            });
         });
         this._logger.info("Saving " + this._entryArray.length + " entries into database.");
         this._setInfoToStorage()
            .then((res) => {
               resolve(res);
            })
            .catch((e) => {
               resolve(null);
            });
      });
   };

   /*
    *---------------------------------------------------------------------------
    * _setInfoToStorage --
    *
    * Save the folder entries to indexedDB
    *
    * Results:
    *      None
    *---------------------------------------------------------------------------
    */
   private _setInfoToStorage = () => {
      return new Promise((resolve, reject) => {
         const cdrFeatureKey = this._getKeyName();
         try {
            this.fileSystemService
               .saveDirectoryEntryInStorage(cdrFeatureKey, this._entryArray)
               .then((res) => {
                  this._logger.info("successfully to save file entries into indexedDB.");
                  resolve(res);
               })
               .catch((error) => {
                  this._logger.error("Failed to save file entry into indexedDB");
                  reject(error);
               });
         } catch (e) {
            reject(e);
         }
      });
   };

   /*
    *---------------------------------------------------------------------------
    * _getKeyName --
    *
    * Get feature name, it should be userName + featureName
    *
    * Results:
    *     string, userName + featureName
    *---------------------------------------------------------------------------
    */

   private _getKeyName = (): string => {
      let username = this.connectionServerModel.username;
      if (!username) {
         // Read user name from cookie
         username = this.userInfoService.getUserName();
         if (!username) {
            // If username is not available, use token username
            // instead.
            username = this.connectionServerModel.tokenUsername;
         }
         // Cache user name in the server model.
         this.connectionServerModel.username = username;
      }
      username = UsernameUtil.getUsername(username);
      this._logger.debug("username name: " + username);
      return username + "sharefolder";
   };

   /*
    *---------------------------------------------------------------------------
    * _getValidEntries --
    *
    * This for folder redirected on blast window, the entries are used for handle
    * messages between client and OS.
    *
    * Results:
    *      None
    *---------------------------------------------------------------------------
    */

   public getValidEntries = async () => {
      let cdrAndFAEntryArray = [];
      if (this._FAFolder) {
         cdrAndFAEntryArray.push(this._FAFolder);
         this._entryArray.forEach((entry) => {
            cdrAndFAEntryArray.push(entry);
         });
         this._logger.debug("Get " + cdrAndFAEntryArray.length + " valid folder entries for CDR + FA");
         return cdrAndFAEntryArray;
      } else {
         this._logger.debug("Get " + this._entryArray.length + " valid folder entries for CDR");
         return this._entryArray;
      }
   };

   public addFAFolder = async (path, entry) => {
      this._FAFolder = entry;
      this.isContainsFA = true;
   };

   public removeFAEntry = () => {
      this._FAFolder = null;
   };
}
